import { AlertCircle } from 'lucide-react';
import beeIcon from '../../assets/icons/assistant-bee.png';
import owlIcon from '../../assets/icons/assistant-owl.png';
import squirrelIcon from '../../assets/icons/assistant-squirrel.png';
import { MarkdownRenderer } from './MarkdownRenderer';
import PromptItemRenderer from './PromptItemRenderer';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  model?: 'norm' | 'exp' | 'eco'; // AI model used (for assistant messages)
  isError?: boolean; // Whether this is an error message
  isWarning?: boolean; // Whether this is a warning message (e.g., aborted)
  userImage?: string; // User's profile image URL
}

const MODEL_INFO = {
  norm: { name: 'Bee', icon: beeIcon },
  exp: { name: 'Owl', icon: owlIcon },
  eco: { name: 'Squirrel', icon: squirrelIcon }
};

export default function ChatMessage({ role, content, timestamp, model, isError = false, isWarning = false, userImage }: ChatMessageProps) {
  const isUser = role === 'user';
  const modelInfo = model ? MODEL_INFO[model] : null;
  
  // Format timestamp
  const formatTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="py-6 px-6">
      <div className={`flex items-start gap-3 ${
        isUser 
          ? 'p-4 bg-white dark:bg-gray-800/40 border border-gray-300 dark:border-gray-700 rounded-xl shadow-sm' 
          : isError 
          ? 'p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl'
          : isWarning
          ? 'p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl'
          : ''
      }`}>
        {/* Avatar/Icon */}
        {isUser ? (
          userImage ? (
            <img 
              src={userImage} 
              alt="User" 
              className="flex-shrink-0 w-8 h-8 rounded-full object-cover" 
            />
          ) : (
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold bg-primary-500 text-white">
              U
            </div>
          )
        ) : isError ? (
          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-red-500/20">
            <AlertCircle className="w-5 h-5 text-red-400" />
          </div>
        ) : isWarning ? (
          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-yellow-500/20">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
          </div>
        ) : modelInfo ? (
          <img 
            src={modelInfo.icon} 
            alt={modelInfo.name} 
            className="flex-shrink-0 w-8 h-8 object-contain rounded-full" 
          />
        ) : (
          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-dark-text-muted">
            AI
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-sm font-semibold ${
              isError 
                ? 'text-red-500 dark:text-red-400' 
                : isWarning 
                ? 'text-yellow-600 dark:text-yellow-400' 
                : 'text-gray-900 dark:text-dark-text'
            }`}>
              {isUser ? 'You' : isError ? 'Error' : isWarning ? 'Aborted' : (modelInfo ? modelInfo.name : 'Assistant')}
            </span>
            <span className="text-xs text-gray-500 dark:text-dark-text-muted">
              {formatTime(timestamp)}
            </span>
          </div>
          {isUser || isError || isWarning ? (
            <div className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${
              isError 
                ? 'text-red-600 dark:text-red-300' 
                : isWarning 
                ? 'text-yellow-700 dark:text-yellow-300' 
                : 'text-gray-700 dark:text-dark-text'
            }`}>
              {isUser ? (
                <PromptItemRenderer text={content} />
              ) : (
                content
              )}
            </div>
          ) : (
            <MarkdownRenderer 
              content={content} 
              className={`text-sm leading-relaxed break-words text-gray-700 dark:text-dark-text`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

